import React, { useState, useEffect } from "react";
import { Button, MenuButton } from "components/button";
import { ContainerWithSidebar } from "components/layout";
import { GoSignIn } from "react-icons/go";
import axios from "axios";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "styles/theme";

import "./index.scss";
import { useModalContext } from "components/modal";
import RemoteStatus from "./components/StatusDetails";
import StatusDetailsModal from "./components/StatusDetails";
import InvoiceStatus from "./components/InvoiceStatus";

// Custom container that accounts for sticky tab bar
const PurchasingContainerWithSidebar = styled(ContainerWithSidebar)`
  > div:first-child {
    top: calc(${theme.height.navigation} + 45px); // navigation + tab bar height
    height: calc(100vh - ${theme.height.navigation} - 45px - 20px); // subtract extra buffer for margins/padding
  }
`;

const Receive = (props) => {
  const { t } = useTranslation();
  const tBase = "views.purchasing.receive.index";
  const tr = (key) => t(`${tBase}.${key}`);
  const { setModal } = useModalContext();

  const [receiveList, setReceiveList] = useState([]);
  const [currentPo, setCurrentPo] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("Submitted");

  const fetchReceive = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/fetchReceive`
      );

      setReceiveList(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchReceiveOne = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/fetchReceive/${id}`
      );

      if (response.data[0]) {
        setCurrentPo(response.data[0]);
        setCurrentStatus("Submitted (Not Shipped)");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const closeReceive = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/closeReceive`,
        { poId: currentPo.id }
      );

      setCurrentPo(null);
      fetchReceive();
      props.fetchCounts();
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickStatus = () => {
    setModal({
      label: "Status",
      component: <StatusDetailsModal />,
    });
  };

  useEffect(() => {
    fetchReceive();
  }, []);

  return (
    <PurchasingContainerWithSidebar>
      <div className="purchasing-sidebar" style={{ backgroundColor: "#edf1f4" }}>
        <div className="review-menu-title">{tr("Outstanding Orders")}</div>
        <hr />
        {receiveList?.map((order) => {
          return (
            <MenuButton
              onClick={() => {
                fetchReceiveOne(order.id);
              }}
              style={{
                textAlign: "left",
                marginTop: 3,
                marginBottom: 3,
                fontSize: "90%",
              }}
              key={order.id}
            >
              <span>{order.po}</span>
              <span>
                {" ("}
                {order.hasOwnProperty("vendor_name")
                  ? order.vendor_name
                  : order.vendor}
                {")"}
              </span>
            </MenuButton>
          );
        })}
      </div>
      <div className="receive-body">
        {currentPo ? (
          <div>
            <div className="receive-top-row">
              <div className="po-label-group">
                <div className="po-label">{currentPo.po}</div>
                {currentPo.timestamp && (
                  <div className="po-created">
                    Created:{" "}
                    {new Date(currentPo.timestamp).toLocaleDateString()}
                  </div>
                )}

                <StatusDetailsModal orderId={currentPo.id} />
                <InvoiceStatus orderId={currentPo.id} />

              </div>
              <Button onClick={() => closeReceive()} primary>
                Close
              </Button>
            </div>
            {currentPo.hasOwnProperty("items") &&
              currentPo.items &&
              currentPo.items.length > 0 &&
              currentPo.items
                .sort((a, b) => {
                  return a.item_id > b.item_id;
                })
                ?.map((item) => {
                  return (
                    <div key={item.item_id} className="receive-row">
                      <div className="receive-item-group">
                        <div className="receive-item-label">
                          {item.pc_name && item.category_name && (
                            <div>{`${item.pc_name} -> ${item.category_name}`}</div>
                          )}
                          <div>{item.item_name}</div>
                        </div>
                      </div>

                      <div className="quantity-group">
                        <div className="receive-quantity-stack">
                          <div>{tr("Ordered")}</div>
                          <div>{item.quantity}</div>
                        </div>

                        <div className="receive-quantity-stack">
                          <div>{tr("Received")}</div>
                          <div>{item.received}</div>
                        </div>

                        <div className="receive-quantity-stack">
                          <div>{tr("Outstanding")}</div>
                          <div>{item.quantity - item.received}</div>
                        </div>
                      </div>

                      <div className="receive-input-group">
                        <ReceiveInput
                          quantity={item.quantity}
                          received={item.received}
                          poId={currentPo.id}
                          itemId={item.item_id}
                          fetchReceive={fetchReceive}
                          fetchReceiveOne={fetchReceiveOne}
                        />
                      </div>
                    </div>
                  );
                })}
          </div>
        ) : null}
      </div>
    </PurchasingContainerWithSidebar>
  );
};

const ReceiveInput = (props) => {
  const { t } = useTranslation();
  const tBase = "views.purchasing.receive.index";
  const tr = (key) => t(`${tBase}.${key}`);

  const [receiveQuantity, setReceiveQuantity] = useState(
    props.quantity - props.received
  );

  useEffect(() => {
    setReceiveQuantity(props.quantity - props.received);
  }, [props.fetchReceive]);

  const receive = async (poId, itemId, quantity) => {
    await axios.put(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/receive`,
      {
        poId,
        itemId,
        quantity,
      }
    );

    props.fetchReceiveOne(poId);
  };

  return (
    <>
      <span>
        {tr("Receive")}:
        <input
          value={receiveQuantity}
          onChange={(e) => {
            setReceiveQuantity(e.target.value);
          }}
          className="receive-input"
        />
      </span>
      <GoSignIn
        className="receive-icon"
        color="green"
        size={22}
        onClick={() => {
          receive(props.poId, props.itemId, receiveQuantity);
        }}
      />
    </>
  );
};


export default Receive;
